<template>
  <div class="lesson-attendance-overview">
    <ag-grid-table
      class="ag-grid"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :row-data="rowData"
      :paginate="false"
      :fit-cols="false"
      :filter="true"
      :search="true"
      :explanation="true"
      @gridSizeChanged="gridSizeChanged"
      @showFilter="showFilterChange"
    >
      <template slot="filter">
        <slot
          name="filter"
        />
      </template>
      <template slot="explanation">
        <div style="text-align: center;">
          <a
            style="cursor: pointer"
            @click="showDetails = true"
          >
            <span
              id="explanation_chevron"
              class="fa fa-info-circle"
            /> {{ trans('core.explanation') }}
          </a>
        </div>
      </template>
    </ag-grid-table>

    <modal
      v-if="showDetails"
      :open="showDetails"
      :title="trans('core.explanation')"
      style="text-align:left; z-index: 9999999999"
      @close="showDetails = false"
    >
      <p>
        {{ trans('courses.matrixoverview_explanation') }}
      </p>
      <div>
        <table style="margin: 0 15px; width:100% ">
          <tr>
            <td>
              <span
                class="fa fa-check"
                :style="{ 'color': COLORS.attended }"
                aria-hidden="true"
              />
            </td>
            <td>{{ trans('core.attended') }}</td>
          </tr>
          <tr>
            <td>
              <span
                class="fa fa-check"
                :style="{ 'color': COLORS.attended_not_schedule }"
                aria-hidden="true"
              />
            </td>
            <td>{{ trans('core.attended_outside_schedule') }}</td>
          </tr>
          <tr>
            <td>
              <span
                class="fa fa-remove"
                :style="{ 'color': COLORS.absent }"
                aria-hidden="true"
              />
            </td>
            <td>{{ trans('core.absent') }}</td>
          </tr>
          <tr v-if="onlyRegisteredClassesCount">
            <td>
              <span
                class="fa fa-bell-slash"
                :style="{ 'color': COLORS.defaultGrey }"
                aria-hidden="true"
              />
            </td>
            <td>{{ trans('core.not_registered') }}</td>
          </tr>
          <tr v-else>
            <td>
              <span
                class="fa fa-remove"
                :style="{ 'color': COLORS.notRegistered }"
                aria-hidden="true"
              />
            </td>
            <td>{{ trans('core.absent_not_registered') }}</td>
          </tr>
          <tr>
            <td>
              <span
                class="fa fa-minus"
                :style="{ 'color': COLORS.excused }"
                aria-hidden="true"
              />
            </td>
            <td>{{ trans('core.excused') }}</td>
          </tr>
          <tr>
            <td> <span class="fa fa-square-o" /> </td>
            <td>{{ trans('core.not_in_schedule') }}</td>
          </tr>
        </table>
      </div>
    </modal>

    <modal
      v-if="openModal"
      :open="openModal"
      :title="trans('core.dossier')"
      style="text-align:left; z-index: 9999999999"
      @close="closeModal"
    >
      <h4> {{ trans('student.dossier_at_event', { event: selectedEvent.name, student: selectedStudent.name }) }} </h4>
      <table
        class="table"
        style="margin-top: 10px;"
      >
        <tr>
          <th>{{ trans('core.date') }}</th>
          <td>{{ selectedEvent.displayDate }}</td>
        </tr>
        <tr>
          <th>{{ trans('core.status') }}</th>
          <td>{{ getAttendedStatusString(selectedStatus) }}</td>
        </tr>
      </table>

      <template v-if="selectedRemarks.length > 0">
        <br>
        <h4> {{ trans_choice('core.remarks', 2) }} </h4>

        <table
          class="table"
          style="margin-top: 10px;"
        >
          <tr>
            <th> {{ trans('core.remark') }} </th>
            <th> {{ trans('core.date') }} </th>
            <th> {{ trans('core.author') }} </th>
          </tr>
          <tr
            v-for="remark in selectedRemarks"
            :key="remark.id"
          >
            <td> {{ remark.remark }} </td>
            <td> {{ moment(remark.created_at).format('DD-MM HH:mm') }} </td>
            <td> {{ remark.author ? remark.author : '-' }} </td>
          </tr>
        </table>
      </template>

      <button
        type="button"
        class="btn btn-default pull-right"
        @click="closeModal"
      >
        {{ trans('core.close') }}
      </button>
      <br style="clear:both">
    </modal>
  </div>
</template>

<script>
import 'ag-grid-community/styles/ag-grid.css'
import AgGridTable from '@/employees/components/AgGridTable'
import Modal from '@/employees/AttendanceList/components/Modal'
import AttendanceMatrixHeader from '@/employees/components/Lessons/AttendanceMatrixHeader'
import { mapState } from 'vuex'
import {
  ABSENT,
  ABSENT_NOT_REGISTERED,
  ATTENDED,
  ATTENDED_NOT_SCHEDULE,
  COLORS,
  COURSE_CONTEXT_EXEMPTION,
  EXCUSED,
  NOT_REGISTERED,
} from '@/constants'
import { studentSortColumns } from '@/helpers'

export default {
  name: 'AttendanceMatrixOverview',
  components: { AgGridTable, Modal, AttendanceMatrixHeader },

  props: {
    events: {
      required: true,
      type: Array,
    },
    students: {
      required: false,
      type: Array,
      default: null,
    },
    courseTitle: {
      required: false,
      type: String,
      default: null,
    },
    withCourseExemption: {
      required: false,
      type: Boolean,
      default: false,
    },
    onlyRegisteredClassesCount: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      gridOptions: {
        defaultColDef: {
          sortable: false,
          resizable: false,
        },
        headerHeight: 60,
        rowBuffer: 25,
        rowHeight: 60,
        suppressCellFocus: true,
        suppressHeaderFocus: true,
        enableBrowserTooltips: true,
        pagination: false,
      },
      pinning: true,
      selectedStudent: null,
      selectedEvent: null,
      selectedStatus: null,
      selectedRemarks: [],
      openModal: false,
      showDetails: false,
    }
  },

  computed: {
    COLORS () {
      return COLORS
    },
    ...mapState({
      storeStudents (state) {
        return state.students.students
      },

      allEvents (state) {
        return state.events.events
      },

      eventStudents (state) {
        return Object.values(state.events.event_students)
      },

      contexts (state) {
        return state.events.student_event_contexts
      },

      courseContexts (state) {
        return state.subscribables.studentCourseContexts
      },
      storeCourses (state) {
        return Object.values(state.courses.courses)
      },

      groups (state) {
        return state.groups.groups
      },
    }),

    eventGroupIds () {
      const ids = Object.values(this.allEvents).map(event => event.groups).flat()

      return _.uniq(ids)
    },

    columnDefs () {
      const events = this.events
      const eventColumns = events.sort((a, b) => new Date(a.starttime) - new Date(b.starttime)) // sort ascending
        .map(event => ({
          headerName: event.name,
          field: `${event.id}`,
          cellRenderer: this.renderAttendedCell,
          headerComponent: 'AttendanceMatrixHeader',
          headerComponentParams: { id: event.id, name: this.transformEventName(event.name), link: event.link, date: event.dense_date },
          cellClass: 'status',
          getQuickFilterText: () => null,
          width: 100,
          cellStyle: this.cellStyle,
        }))

      const columns = [{
        headerName: Lang.get('core.name'),
        field: 'name',
        pinned: 'left',
        cellRenderer: this.renderStudentCell,
        valueFormatter: params => params.value.name,
        getQuickFilterText: (params) => {
          return params.value
        },
        cellStyle: this.cellStyle,
      }]

      if (this.withCourseExemption) {
        columns.push({
          headerName: Lang.get('student.course_context.types.exemption'),
          field: 'name',
          pinned: 'left',
          cellRenderer: this.renderExemptionCell,
          getQuickFilterText: () => null,
          cellClass: 'status',
          width: 100,
          cellStyle: this.cellStyle,
        })
      }

      columns.push(...eventColumns)
      columns.push({
        headerName: Lang.get('core.percentage'),
        field: 'percentage',
        pinned: this.pinning ? 'right' : null,
        width: 100,
        cellStyle: this.cellStyle,
      })
      columns.push({
        headerName: Lang.get('core.total'),
        field: 'totals',
        pinned: this.pinning ? 'right' : null,
        width: 100,
        cellStyle: this.cellStyle,
      })

      return columns
    },

    cellStyle () {
      return { 'font-size': '14px', padding: '5px', 'font-family': 'Roboto' }
    },

    rowData () {
      const studentEventData = _.groupBy(this.eventStudents, 'student_id')

      return this.sortedStudents.map((student) => {
        const attendanceRows = []
        let max = 0
        let attended = 0

        if (student.courseContexts != null) {
          student.hasCourseExemption = student.courseContexts
            .map(courseContextId => this.courseContexts[courseContextId])
            .filter(courseContext => courseContext.type === COURSE_CONTEXT_EXEMPTION)
            .length > 0
        }

        this.events.forEach((event) => {
          const attendanceData = studentEventData[student.id] != null ? studentEventData[student.id].find(data => data.event_id == event.id) : null
          const studentEventContexts = event.contexts.map(contextId => this.contexts[contextId]).filter(context => context.student_id == student.id)
          let attendedStatus = null

          if (attendanceData && attendanceData.attended) {
            if (attendanceData.should_attend) {
              attendedStatus = ATTENDED
            } else {
              attendedStatus = ATTENDED_NOT_SCHEDULE
            }
            attended++
            max++
          } else if (attendanceData && attendanceData.should_attend && !attendanceData.attended) {
            if (event.attendance_registered === true) {
              attendedStatus = ABSENT
            } else if (this.onlyRegisteredClassesCount) {
              attendedStatus = NOT_REGISTERED
            } else {
              attendedStatus = ABSENT_NOT_REGISTERED
            }
            max++
          } else if (attendanceData && attendanceData.excused) {
            attendedStatus = EXCUSED
          }

          attendanceRows[event.id] = {
            event_id: event.id,
            student_id: student.id,
            attended_status: attendedStatus,
            remarks: studentEventContexts,
          }
        })

        const percentage = max > 0 ? Math.round((attended / max) * 100) : null

        return {
          name: student,
          ...attendanceRows,
          percentage: percentage !== null ? `${percentage}%` : '-',
          totals: `${attended} / ${max}`,
        }
      })
    },

    sortedStudents () {
      // use the given students if applicable
      if (this.students) {
        return _.orderBy(this.students, studentSortColumns())
      }

      return _.orderBy(Object.values(this.storeStudents), studentSortColumns())
    },
  },

  methods: {
    renderAttendedCell (data) {
      const div = document.createElement('div')

      const status = data.value.attended_status
      const remarkCount = data.value.remarks.length

      const remarkCountAtrr = `data-count="${remarkCount}"`
      const classname = (remarkCount !== 0 ? ' fa-icon-badge' : '')
      const style = 'padding-right: 8px;position: relative;padding-top: 8px;cursor:pointer;'

      if (status === ATTENDED) {
        div.innerHTML = `<span class="fa fa-check ${classname} detailsbutton" style="color:${COLORS.attended}; ${style}" aria-hidden="true" ${remarkCountAtrr}
        title="${Lang.get('core.attended')}"></span>`
      } else if (status === ATTENDED_NOT_SCHEDULE) {
        div.innerHTML = `<span class="fa fa-check ${classname} detailsbutton" style="color:${COLORS.attended_not_schedule}; ${style}" aria-hidden="true" ${remarkCountAtrr}
        title="${Lang.get('core.attended_outside_schedule')}"></span>`
      } else if (status === ABSENT) {
        div.innerHTML = `<span class="fa fa-remove ${classname} detailsbutton" style="color:${COLORS.absent}; ${style}" aria-hidden="true" ${remarkCountAtrr}
        title="${Lang.get('core.absent')}"></span>`
      } else if (status === NOT_REGISTERED) {
        div.innerHTML = `<span class="fa fa-bell-slash ${classname} detailsbutton" style="color:${COLORS.defaultGrey}; ${style}" aria-hidden="true" ${remarkCountAtrr}
        title="${Lang.get('core.not_registered')}"></span>`
      } else if (status === ABSENT_NOT_REGISTERED) {
        div.innerHTML = `<span class="fa fa-remove ${classname} detailsbutton" style="color:${COLORS.absent_not_registered}; ${style}" aria-hidden="true" ${remarkCountAtrr}
        title="${Lang.get('core.absent_not_registered')}"></span>`
      } else if (status === EXCUSED) {
        div.innerHTML = `<span class="fa fa-minus ${classname} detailsbutton" style="color:${COLORS.excused}; ${style}" aria-hidden="true" ${remarkCountAtrr}
        title="${Lang.get('core.excused')}"></span>`
      }

      const button = div.querySelectorAll('.detailsbutton')[0]

      if (button !== undefined) {
        button.addEventListener('click', () => {
          this.selectedStudent = this.students.find(student => student.id == data.value.student_id)
          this.selectedEvent = this.allEvents[data.value.event_id]
          this.selectedRemarks = data.value.remarks
          this.selectedStatus = status
          this.openModal = true
        })
      }

      return div
    },

    renderStudentCell (student) {
      const div = document.createElement('div')

      const groupNames = student.value.groups.filter(groupId => this.eventGroupIds.includes(groupId)).map(groupId => this.groups[groupId].name).join(', ')

      div.innerHTML = `<span class="studentbutton" style="cursor:pointer"><b>${_.escape(student.value.name)}</b>` + (groupNames.length < 1 ? '' : `<br><small>${_.escape(groupNames)}</small></span>`)

      if (student.value.link) {
        const button = div.querySelectorAll('.studentbutton')[0]

        button.addEventListener('click', function () {
          window.open(student.value.link)
        })
      }

      return div
    },

    renderExemptionCell (student) {
      const div = document.createElement('div')

      const style = 'padding-right: 8px;position: relative;padding-top: 8px;'
      if (student.value.hasCourseExemption) {
        div.innerHTML = `<span class="fa fa-check" style="color:${COLORS.attended}; ${style}" aria-hidden="true" title="${Lang.get('student.course_context.tooltips.exemption')}"></span>`
      } else {
        div.innerHTML = `<span class="fa fa-remove" style="color:${COLORS.absent}; ${style}" aria-hidden="true"></span>`
      }

      return div
    },

    gridSizeChanged (data) {
      const availableWidth = data.clientWidth
      const totalColumnWidth = data.api.getAllDisplayedColumns()
        .reduce((total, column) => total + column.actualWidth, 0)

      this.pinning = availableWidth < totalColumnWidth
    },

    transformEventName (name) {
      if (this.courseTitle) {
        const replacement = name === this.courseTitle ? Lang.get('core.class') : ''
        return name.replace(this.courseTitle, replacement)
      }

      return name
    },

    getAttendedStatusString (status) {
      if (status === ATTENDED) {
        return Lang.get('core.attended')
      } else if (status === ATTENDED_NOT_SCHEDULE) {
        return Lang.get('core.attended_outside_schedule')
      } else if (status === ABSENT) {
        return Lang.get('core.absent')
      } else if (status === NOT_REGISTERED) {
        return Lang.get('core.not_registered')
      } else if (status === ABSENT_NOT_REGISTERED) {
        return Lang.get('core.absent_not_registered')
      } else if (status === EXCUSED) {
        return Lang.get('core.excused')
      } else {
        return '-'
      }
    },

    closeModal () {
      this.selectedStudent = null
      this.selectedEvent = null
      this.selectedRemarks = []
      this.selectedStatus = null
      this.openModal = false
    },
    showFilterChange (event) {
      this.$emit('showFilter', event)
    },
  },
}
</script>

<style lang="scss">
  .lesson-attendance-overview {
    flex: 0 1 auto;
    display: flex;
    flex-direction: column;
    height: inherit;

    .ag-grid {
      display: flex;
      flex: 1 1 auto;
      overflow-y: auto;
      overflow-x: auto;
      height: inherit;
    }

    .ag-row .ag-cell {
      display: flex;
      align-items: center;

      &.status {

        justify-content: center; /* align horizontal */

      }
    }

    .ag-row {
      border-top: 1px solid #dee2e6;
    }

    .ag-cell {
      line-height: 20px !important;
    }

    .ag-horizontal-left-spacer, .ag-horizontal-right-spacer {
      visibility: hidden;
    }
  }

  /* badge counter */
  .fa-icon-badge:after {
    content: attr(data-count);
    font-family: Roboto, Helvetica, Arial, sans-serif;
    position: absolute;
    background: rgba(0, 0, 0, 1);
    height: 1.3rem;
    top: -0.2rem;
    right: -0.4rem;
    width: 1.3rem;
    padding-right: 0.1rem;
    padding-left: 0.0rem;
    text-align: center;
    line-height: 1.1rem;
    font-size: 1.1rem;
    border-radius: 50%;
    color: white;
    border: 1px solid rgba(0, 0, 0, 1);
  }
</style>
